import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import CommissionFilters from './CommissionFilters';
import CommissionDetailDialog from './CommissionDetailDialog';
import { commissionApi } from '../../services/api';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

const getStatusChip = (status) => {
  const config = {
    pending: { label: 'Pending', color: 'warning' },
    auto_approved: { label: 'Auto Approved', color: 'success' },
    manually_approved: { label: 'Manually Approved', color: 'success' },
    declined: { label: 'Declined', color: 'default' },
  };
  const { label, color } = config[status] || { label: status, color: 'default' };
  return <Chip label={label} color={color} size="small" />;
};

const getMatchLevelChip = (level) => {
  const config = {
    PERFECT_MATCH: { label: 'Perfect', color: 'success' },
    COLOR_PRODUCT_MATCH: { label: 'Color+Product', color: 'info' },
    COLOR_RETAILER_MATCH: { label: 'Color+Retailer', color: 'warning' },
    RETAILER_ONLY_MATCH: { label: 'Retailer Only', color: 'error' },
    NO_MATCH: { label: 'No Match', color: 'default' },
  };
  const { label, color } = config[level] || { label: level, color: 'default' };
  return <Chip label={label} color={color} size="small" variant="outlined" />;
};

export default function CommissionList({ onRefresh }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [retailers, setRetailers] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const [filters, setFilters] = useState({
    selling_retailer_id: '',
    buying_retailer_id: '',
    status: 'pending',
    match_level: '',
    from_date: '',
    to_date: '',
    invoice_number: '',
  });

  const [sortModel, setSortModel] = useState([
    { field: 'order_timestamp', sort: 'desc' },
  ]);

  const fetchRetailers = useCallback(async () => {
    try {
      const result = await commissionApi.getRetailers();
      if (result.success) {
        setRetailers(result.retailers);
      }
    } catch (err) {
      console.error('Failed to fetch retailers:', err);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        limit: pageSize,
        offset: page * pageSize,
        sort_by: sortModel[0]?.field || 'order_timestamp',
        sort_order: sortModel[0]?.sort || 'desc',
      };

      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const result = await commissionApi.list(params);

      if (result.success) {
        setItems(result.items);
        setTotal(result.total);
      } else {
        setError(result.error || 'Failed to fetch commissions');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, sortModel]);

  useEffect(() => {
    fetchRetailers();
  }, [fetchRetailers]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0); // Reset to first page on filter change
  };

  const handleClearFilters = () => {
    setFilters({
      selling_retailer_id: '',
      buying_retailer_id: '',
      status: '',
      match_level: '',
      from_date: '',
      to_date: '',
      invoice_number: '',
    });
    setPage(0);
  };

  const handleRefresh = () => {
    fetchItems();
    if (onRefresh) onRefresh();
  };

  const handleRowClick = (params) => {
    setSelectedItem(params.row);
    setDetailDialogOpen(true);
  };

  const handleDetailClose = (refreshNeeded) => {
    setDetailDialogOpen(false);
    setSelectedItem(null);
    if (refreshNeeded) {
      handleRefresh();
    }
  };

  const columns = [
    {
      field: 'order_number',
      headerName: 'Order #',
      width: 120,
      renderCell: (params) => (
        <Tooltip title={params.row.id}>
          <span>{params.value || '-'}</span>
        </Tooltip>
      ),
    },
    {
      field: 'product_name',
      headerName: 'Product',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {params.value || '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'match_level',
      headerName: 'Match',
      width: 130,
      renderCell: (params) => getMatchLevelChip(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'commission_amount',
      headerName: 'Commission',
      width: 120,
      renderCell: (params) => {
        if (params.value == null) return '-';
        const currency = params.row.currency || 'EUR';
        return `${params.value.toFixed(2)} ${currency}`;
      },
    },
    {
      field: 'buying_retailer_name',
      headerName: 'Buying',
      width: 130,
      renderCell: (params) => params.value || params.row.buying_retailer_id,
    },
    {
      field: 'selling_retailer_name',
      headerName: 'Selling',
      width: 130,
      renderCell: (params) => params.value || params.row.selling_retailer_id,
    },
    {
      field: 'order_timestamp',
      headerName: 'Order Date',
      width: 130,
      valueFormatter: (value) =>
        value ? format(new Date(value), 'MMM d, yyyy') : '-',
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice',
      width: 120,
      renderCell: (params) => {
        const status = params.row.status;
        const isApproved = status === 'auto_approved' || status === 'manually_approved';

        // Only show invoice info for approved items
        if (!isApproved) {
          return <span style={{ color: '#999' }}>-</span>;
        }

        if (!params.row.invoice_id) {
          return <Chip label="Not invoiced" size="small" variant="outlined" />;
        }

        return (
          <Tooltip title="View Invoice">
            <Chip
              label={params.value || 'View'}
              size="small"
              color="success"
              icon={<ReceiptIcon />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/invoicing?id=${params.row.invoice_id}`);
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedItem(params.row);
              setDetailDialogOpen(true);
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <CommissionFilters
        filters={filters}
        retailers={retailers}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        onRefresh={handleRefresh}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ height: { xs: 400, md: 600 }, width: '100%' }}>
        <DataGrid
          rows={items}
          columns={columns}
          loading={loading}
          rowCount={total}
          pageSizeOptions={[25, 50, 100]}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            if (model.length > 0) {
              setSortModel(model);
            }
          }}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
          }}
        />
      </Paper>

      {/* Detail Dialog */}
      <CommissionDetailDialog
        open={detailDialogOpen}
        item={selectedItem}
        onClose={handleDetailClose}
      />
    </Box>
  );
}
